import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '../utilities/snackbar_provider';
import axios from 'axios';

function extractErrorMessage(res, fallback = 'Action failed') {
  if (axios.isAxiosError(res) && res.response) {
    const d = res.response.data;
    if (typeof d === 'string') return d;

    // Zod middleware shape: { error: "Validation failed", details: [{field, message}] }
    const detailMessages = Array.isArray(d?.details)
      ? d.details.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
      : [];
    if (detailMessages.length) return detailMessages.join('. ');

    // Standard error handler shape: { message: "...", errors: [{field, message}] }
    const errorMessages = Array.isArray(d?.errors)
      ? d.errors.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
      : [];
    if (errorMessages.length) return errorMessages.join('. ');

    return typeof d?.error === 'string' ? d.error
      : d?.error?.message || d?.msg || d?.message || fallback;
  }
  return res?.message || fallback;
}

/**
 * useApiMutation — TanStack Query wrapper for ApiService POST/PUT/DELETE functions.
 *
 * USAGE
 * ─────
 *   const { mutate: createEmployee, isPending } = useApiMutation(
 *     ApiService.createEmployee,
 *     {
 *       successMessage: 'Employee created!',
 *       invalidateKeys: [['employees']],   // auto-refetch these queries on success
 *     }
 *   );
 *   // call: mutate(payload)
 *
 *   // For APIs that take (body, id) — pass a wrapper function:
 *   const { mutate: updateEmployee, isPending } = useApiMutation(
 *     ({ body, id }) => ApiService.updateEmployee(body, id),
 *     { successMessage: 'Employee updated!', invalidateKeys: [['employees']] }
 *   );
 *   // call: mutate({ body: { name: 'John' }, id: '123' })
 *
 * MIGRATION from useApi
 * ──────────────────────
 *   BEFORE:
 *     const { action: save, loading } = useApi(ApiService.createEmployee, {
 *       success: true, actionName: 'Employee'
 *     });
 *
 *   AFTER:
 *     const { mutate: save, isPending: loading } = useApiMutation(
 *       ApiService.createEmployee,
 *       { successMessage: 'Employee created!' }
 *     );
 *
 * @param {Function}  apiFunc   ApiService function OR a wrapper: (variables) => Promise
 * @param {Object}    [options]
 *   successMessage  {string}    Snackbar message shown on success.
 *   errorMessage    {string}    Fallback snackbar message on error. Default: 'Action failed'.
 *   invalidateKeys  {Array}     Query keys to invalidate (and refetch) after success.
 *   onSuccess       {Function}  (data) => void — called after success.
 *   onError         {Function}  (message: string) => void — called after error.
 *   showErrorSnackbar {boolean} Show error snackbar automatically. Default: true.
 *   ...rest         Any other TanStack useMutation options.
 */
const useApiMutation = (apiFunc, options = {}) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const {
    successMessage,
    errorMessage = 'Action failed',
    invalidateKeys = [],
    onSuccess,
    onError,
    showErrorSnackbar = true,
    ...mutationOptions
  } = options;

  return useMutation({
    mutationFn: async (variables) => {
      const res = await apiFunc(variables);
      if (axios.isAxiosError(res) || res instanceof Error) {
        throw new Error(extractErrorMessage(res, errorMessage));
      }
      return res;
    },
    onSuccess: (data) => {
      if (successMessage) showSnackbar(successMessage, 'success');
      invalidateKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
      );
      onSuccess?.(data);
    },
    onError: (err) => {
      if (showErrorSnackbar) showSnackbar(err.message || errorMessage, 'error');
      onError?.(err.message);
    },
    ...mutationOptions,
  });
};

export default useApiMutation;
